import { Card } from "@/components/ui/card";
import { Notify } from "@/components/ui/notify";
import { useLineMachines, type MachineDetail } from "@/features/lines/hooks";
import type { LineStatus } from "@opus-dt/shared-types";
import { useActiveRun } from "@/features/production/hooks";
import { AttentionStrip } from "./components/AttentionStrip";
import { VitalsBar } from "./components/VitalsBar";
import { useLinePeriodFilter } from "./components/LineKPIStripPeriodFilter";
import { EventsList } from "./components/EventsList";
import { ActiveRunCard } from "./components/ActiveRunCard";
import { BatchesCard } from "@/features/batches/BatchesCard";
import { AttentionCard } from "./components/AttentionCard";
import { LossesCard } from "./components/LossesCard";
import { ManualEntriesCard } from "./components/ManualEntriesCard";
import { FlowSectionCard } from "./components/FlowSectionCard";
import { MachineDecompositionCard } from "./components/MachineDecompositionCard";
import { useFirstOfflineMachine } from "./useFirstOfflineMachine";

// Spec 083 (T042) — a VISÃO GERAL do detalhe da linha, extraída da página sem alteração nenhuma.
//
// A extração existe porque a página chegou ao teto de 200 linhas efetivas e a aba nova a estouraria.
// Nenhum cartão mudou de lugar, de ordem ou de comportamento: é a mesma tela, noutro arquivo.
//
// As consultas moram aqui, e não descem por props. `useLineMachines` é chamado também pela página,
// para resolver o link profundo `?machine=` — TanStack dedupe pela chave, então são duas leituras do
// mesmo cache e uma requisição só.

interface Props {
  lineId: string;
  lineName: string;
  lineStatus: LineStatus;
  onSelectMachine: (machine: MachineDetail) => void;
}

export function LineOverviewTab({ lineId, lineName, lineStatus, onSelectMachine }: Props) {
  const [period, setPeriod] = useLinePeriodFilter();
  const {
    data: run,
    isError: runIsError,
    error: runError,
    refetch: runRefetch,
  } = useActiveRun(lineId);
  const {
    data: machines,
    isError: machinesIsError,
    error: machinesError,
    refetch: machinesRefetch,
  } = useLineMachines(lineId);
  const { firstOfflineMachine, offlineFor } = useFirstOfflineMachine(machines);

  // Erro real (500/timeout/etc.) nunca deve virar "não encontrada" (afirmação falsa).
  const isActiveWithoutRun = period === "active" && !run && !runIsError;

  return (
    <>
      <section aria-label="Indicadores da linha">
        {isActiveWithoutRun ? (
          <Notify.Empty
            variant="card"
            title="Sem produção ativa"
            description="Esta linha não tem uma execução em curso. Troque o filtro de período para ver dados históricos."
            action={
              <button
                type="button"
                onClick={() => setPeriod("24h")}
                className="hover:text-brand-800 inline-flex items-center gap-1 rounded text-[13px] font-medium text-brand-700 focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]"
              >
                Mostrar últimos 24h
              </button>
            }
          />
        ) : runIsError ? (
          <Notify.QueryError error={runError} onRetry={() => void runRefetch()} variant="inline" />
        ) : (
          <VitalsBar lineId={lineId} period={period} />
        )}
      </section>

      {firstOfflineMachine ? (
        <div className="mt-3">
          <AttentionStrip
            machine={{ id: firstOfflineMachine.id, name: firstOfflineMachine.name, offlineFor }}
            onInspect={() => onSelectMachine(firstOfflineMachine)}
          />
        </div>
      ) : null}

      <div className="mt-6">
        <FlowSectionCard
          lineId={lineId}
          lineName={lineName}
          lineStatus={lineStatus}
          onMachineClick={onSelectMachine}
        />
      </div>

      {/* Decomposição do OEE por máquina (só em linha rastreada por máquina). */}
      <div className="mt-6">
        {machinesIsError ? (
          <Notify.QueryError
            error={machinesError}
            onRetry={() => void machinesRefetch()}
            variant="inline"
          />
        ) : (
          <MachineDecompositionCard
            lineId={lineId}
            machines={machines ?? []}
            onSelectMachine={onSelectMachine}
          />
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[1.5fr_1fr]">
        <div className="space-y-6">
          <ActiveRunCard lineId={lineId} />
          <BatchesCard lineId={lineId} />

          <section aria-labelledby="events-h2" className="space-y-3">
            <h2
              id="events-h2"
              className="text-[18px] font-semibold tracking-tight text-text-primary"
            >
              Eventos recentes
            </h2>
            <Card className="rounded-2xl px-[22px] py-5">
              <EventsList lineId={lineId} />
            </Card>
          </section>
        </div>

        <div className="space-y-6">
          <LossesCard lineId={lineId} />
          <ManualEntriesCard lineId={lineId} />
          <AttentionCard lineId={lineId} />
        </div>
      </div>
    </>
  );
}
